import { Modal } from "antd";
import type { DraggableData, DraggableEvent } from "react-draggable";
import Draggable from "react-draggable";
import { FC, useState, useRef, useEffect } from "react";
import { IModal } from "../interfaces/IModal";
import { MenuOutlined } from "@ant-design/icons";

const ModalComponent: FC<IModal> = ({
  visible,
  onOK,
  onCancel,
  title,
  children,
  modalWidth = "500px",
  okText,
  cancelText,
  className,
  styles,
  dataTestSelector,
  zIndex,
  isMaskClosable,
  showMask,
  footer,
  subTitle,
  closable = true,
  resizable = false
}) => {
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
  const draggleRef = useRef<HTMLDivElement>(null);
  const [disabled, setDisabled] = useState(true);
  const [size, setSize] = useState({
    width: parseInt(modalWidth.replace("px", ""), 10),
    height: styles?.height
      ? parseInt(`${styles.height}`.replace("px", ""), 10)
      : 'auto',
  });

  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    setSize((prev) => ({
      ...prev,
      width: parseInt(modalWidth.replace("px", ""), 10),
      height: parseInt((styles?.height as string || "auto").replace("px", ""), 10),
    }));
  }, [modalWidth, styles]);

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) {
      return;
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };
  const modalLabel = () => {
    return (
      <div
      style={{
        width: '100%',
        cursor: 'move',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        }}
        onMouseOver={() => {
          if (disabled) {
            setDisabled(false);
          }
        }}
        onMouseOut={() => {
          setDisabled(true);
        }}
        onFocus={() => { }}
        onBlur={() => { }}
      >
        {title}
        {subTitle ? subTitle : null}
      </div>
    )
  }

  const startResize = () => {
    setResizing(true);
  };

  const resize = (e: MouseEvent) => {
    if (!resizing || !draggleRef.current) return;
  
    const modalRect = draggleRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const newWidth = Math.max(400, Math.min(e.clientX - modalRect.left, viewportWidth - modalRect.left));
    const newHeight = Math.max(200, Math.min(e.clientY - (modalRect.top + 80), viewportHeight - (modalRect.top + 80)));

    setSize({
      width: newWidth,
      height: newHeight,
    });
  };

  const stopResize = () => {
    setResizing(false);
  };

  useEffect(() => {
    if (resizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResize);
    } else {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResize);
    }

    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResize);
    };
  }, [resizing]);

  return (
    <Modal
      title={modalLabel()}
      centered={true}
      open={visible}
      onOk={() => {
        onOK && onOK();
        Modal.destroyAll();
      }}
      onCancel={() => {
        onCancel && onCancel();
        Modal.destroyAll();
      }}
      width={size.width}
      okText={okText}
      cancelText={cancelText}
      className={className}
      styles={{
        body: { ...styles, height: `${size.height}px` },
      }}
      data-test-selector={dataTestSelector}
      zIndex={zIndex}
      maskClosable={isMaskClosable}
      mask={showMask}
      footer={footer}
      closable={closable}
      modalRender={(modal) => (
        <Draggable
          disabled={disabled}
          bounds={bounds}
          nodeRef={draggleRef as React.RefObject<HTMLElement>}
          onStart={(event, uiData) => onStart(event, uiData)}
        >
          <div ref={draggleRef}>
            {modal}
          </div>
        </Draggable>
      )}
    >
      {children}
      {resizable && 
        <MenuOutlined className="resize-icon" onMouseDown={startResize} />
        }
    </Modal>
  );
};
export default ModalComponent;

